// The dashboard's numbers shaped for its charts (D-144): who to show, the team's totals, the hours to draw, the heat
// shades, and one person's (or everyone's) calls by hour, half hour (D-145) or day with the quiet ones filled in. Pure
// functions, tested.
import type { DashBucket, DashMember } from "./types";

/** Anything on the phone that day: a call either way, or a follow-up done. */
export function isActive(m: DashMember): boolean {
  return m.dialed + m.received + m.missed + m.follow_ups_done > 0;
}

/** Telecallers always, so a quiet day shows, and anyone else who called; the busiest first. */
export function agentsToShow(members: DashMember[]): DashMember[] {
  return members.filter((m) => m.role === "caller" || isActive(m))
    .sort((a, b) => b.dialed + b.received - (a.dialed + a.received) || a.name.localeCompare(b.name));
}

export type Totals = Omit<DashMember, "member_id" | "name" | "role" | "first_call_at" | "last_call_end_at">;

const SUMMED: (keyof Totals)[] = [
  "dialed",
  "dialed_connected",
  "not_answered",
  "received",
  "missed",
  "talk_seconds",
  "customers_reached",
  "interested",
  "call_back",
  "visits_booked",
  "follow_ups_done",
  "follow_ups_on_time",
];

/** Everyone's numbers added up. A customer two people reached counts for each of them. */
export function teamTotals(members: DashMember[]): Totals {
  const t = Object.fromEntries(SUMMED.map((k) => [k, 0])) as Totals;
  for (const m of members) for (const k of SUMMED) t[k] += m[k];
  return t;
}

/** The share of the calls placed that connected, or null when none were placed. */
export function connectRate(m: { dialed: number; dialed_connected: number }): number | null {
  return m.dialed > 0 ? m.dialed_connected / m.dialed : null;
}

/** "62%", or "–" when there is nothing to divide by. */
export function pct(part: number, whole: number): string {
  return whole > 0 ? `${Math.round((part / whole) * 100)}%` : "–";
}

/** The hours to draw: the usual calling day, 8 am to 9 pm, widened to any hour that had calls. */
export function hourColumns(hours: DashBucket[]): number[] {
  const busy = hours.filter((h) => h.calls > 0).map((h) => h.hour ?? 0);
  const first = Math.min(8, ...busy);
  const last = Math.max(20, ...busy);
  return Array.from({ length: last - first + 1 }, (_, i) => first + i);
}

/** The half hours to draw, as minutes since midnight (600 is 10:00 to 10:30): both halves of every hour drawn. */
export function halfColumns(hours: DashBucket[]): number[] {
  return hourColumns(hours).flatMap((h) => [h * 60, h * 60 + 30]);
}

/** How many shades of emerald the heat map uses for counts above zero. */
export const HEAT_STEPS = 5;

/** A count's shade: 0 for none, then 1 to 5 by its share of the busiest cell (any count above zero shows). */
export function heatStep(value: number, max: number): number {
  if (value <= 0 || max <= 0) return 0;
  return Math.min(HEAT_STEPS, Math.max(1, Math.ceil((value / max) * HEAT_STEPS)));
}

export interface Point {
  key: number | string;
  calls: number;
  connected: number;
  talk_seconds: number;
}

/** A chart's columns: hours of the day, half hours (minutes since midnight) or days. */
export type By = "hour" | "half" | "day";

/** The column a bucket falls in. The server's buckets are half hours, so an hour adds up both of its halves. */
function columnOf(b: DashBucket, by: By): number | string | undefined {
  return by === "half" ? (b.hour ?? 0) * 60 + (b.minute ?? 0) : b[by];
}

/** One person's calls (everyone's with null) in each column; a column without calls is zero. */
export function seriesBy(
  buckets: DashBucket[],
  memberId: string | null,
  columns: (number | string)[],
  by: By,
): Point[] {
  return columns.map((key) => {
    const p: Point = { key, calls: 0, connected: 0, talk_seconds: 0 };
    for (const b of buckets) {
      if ((memberId === null || b.member_id === memberId) && columnOf(b, by) === key) {
        p.calls += b.calls;
        p.connected += b.connected;
        p.talk_seconds += b.talk_seconds;
      }
    }
    return p;
  });
}

/** A person's calls in one column, for the heat map. */
export function cellOf(buckets: DashBucket[], memberId: string, key: number | string, by: By): Point {
  return seriesBy(buckets, memberId, [key], by)[0];
}

/** Talk time: "45 s", "23 min", "2 h 5 min". */
export function fmtTalk(seconds: number): string {
  if (seconds < 60) return `${Math.max(0, Math.round(seconds))} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/** An hour of the day as a label: "8 am", "12 pm", "1 pm". */
export function hourLabel(h: number): string {
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12} ${h < 12 ? "am" : "pm"}`;
}

/** The span of an hour: "11 am – 12 pm". */
export function hourSpan(h: number): string {
  return `${hourLabel(h)} – ${hourLabel((h + 1) % 24)}`;
}

/** A time of day from minutes since midnight: "10:30 am". */
export function clockLabel(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24;
  const m = String(minutes % 60).padStart(2, "0");
  return `${h % 12 === 0 ? 12 : h % 12}:${m} ${h < 12 ? "am" : "pm"}`;
}

/** The span of a half hour: "10:00 – 10:30 am", or "11:30 am – 12:00 pm" across noon. */
export function halfSpan(start: number): string {
  const from = clockLabel(start);
  const to = clockLabel(start + 30);
  return from.slice(-2) === to.slice(-2) ? `${from.slice(0, -3)} – ${to}` : `${from} – ${to}`;
}

/** Clean y-axis ticks from zero to at least the largest value: 0, 5, 10… or 0, 20, 40… */
export function ticks(max: number, count = 4): number[] {
  if (max <= 0) return [0, 1];
  const raw = max / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((f) => f * mag).find((s) => s >= raw) ?? 10 * mag;
  const top = Math.ceil(max / step) * step;
  return Array.from({ length: Math.round(top / step) + 1 }, (_, i) => i * step);
}
